import { useEffect, useMemo, useState } from "react";
import { PostType } from "../../Components/Post/Post.types";
import { useGlobalConfigs } from "@infrastructure/Hooks/useGlobalConfigs";
import { mapPostData } from "../../DataFetchers/PostDataFetcher";
import { getPromoPosts } from "@infrastructure/Services/GroupService";
import { PostResult } from "@infrastructure/Services/PostService.types";
import { useHiddenPromoPostsStore } from "./useHiddenPromoPostsStore";
import {
    ReactionActionState,
    SWITCHING_STATE_TO_IDLE_TIME,
    useReactionActionStore,
} from "../../Components/ReactionsTooltip/ReactionActionState";
import { useOnPostReaction } from "../../Components/Reactions/Hooks/useOnPostReaction";
import { updateUserReactionOnObject } from "@infrastructure/Hooks/UserInteractions/useUserReactions";
import { ReactionableObjectEnum } from "@infrastructure/Hooks/UserInteractions/UserInteractions.types";
import {
    addSubscriber,
    removeSubscriber,
} from "@infrastructure/Events/EventBus";
import { POST_DETAILS_CHANGED } from "../../Components/Post/PostForm/events";
import { REFRESH_LIST_EVENT } from "../../Components/DashboardComponents/PostList/PostsList";

const DELAY_INTERVAL_MILLISECONDS = 60 * 1000;

const pageRelatedProps = {
    boxed: true,
    showPreviewComment: true,
    showCommentButton: true,
    hideImages: false,
    hideActions: false,
    wrapperMarginBottomValue: "32px",
};

const fetchPromoPosts = async (): Promise<PostType[]> => {
    const apiResponse = await getPromoPosts();

    return apiResponse.posts.map(
        (post: PostResult): PostType => ({
            ...mapPostData(post, pageRelatedProps),
            status: {
                isHighlighted: false,
                isEditorial: post.isEditorial,
            },
        }),
    );
};

export const usePromoPosts = () => {
    const [posts, setPosts] = useState<PostType[]>([]);
    const [shouldUpdate, setShouldUpdate] = useState(false);
    const { getConfigs } = useGlobalConfigs();

    useEffect(() => {
        if (!getConfigs()?.tippspielPromotionSegmentPosts) {
            return;
        }

        fetchPromoPosts()
            .then(setPosts)
            .then(() => {
                setShouldUpdate(true);
            });
    }, [getConfigs]);

    useEffect(() => {
        if (!shouldUpdate) {
            return;
        }
        const updatePromoPostsInterval = setInterval(() => {
            fetchPromoPosts().then(setPosts);
        }, DELAY_INTERVAL_MILLISECONDS);

        return () => {
            if (updatePromoPostsInterval) {
                clearInterval(updatePromoPostsInterval);
            }
        };
    }, [shouldUpdate]);

    const { hiddenPosts, isPostHidden } = useHiddenPromoPostsStore();

    const postsToShow = useMemo(
        () => posts.filter((post) => !isPostHidden(post.id)),
        [posts, hiddenPosts],
    );

    const { setReactionActionState } = useReactionActionStore();
    useOnPostReaction(setPosts, (event) => {
        updateUserReactionOnObject(
            event.entityId.toString(),
            event.reactions,
            ReactionableObjectEnum.POST,
        );
        setTimeout(
            () => setReactionActionState(ReactionActionState.IDLE),
            SWITCHING_STATE_TO_IDLE_TIME,
        );
    });

    useEffect(() => {
        const postDetailsChangedSubscriber = addSubscriber(
            POST_DETAILS_CHANGED,
            (eventData) => {
                const data = eventData.body.data as PostType;
                const foundPostIndex = posts.findIndex(
                    ({ id }) => id === data.id,
                );
                if (foundPostIndex >= 0) {
                    const copyPosts = [...posts];
                    copyPosts.splice(foundPostIndex, 1, {
                        ...data,
                        pageRelated: pageRelatedProps,
                    });
                    setPosts(copyPosts);
                }
            },
        );
        return () => {
            removeSubscriber(postDetailsChangedSubscriber);
        };
    }, [posts]);

    useEffect(() => {
        const refreshListSubscriber = addSubscriber(REFRESH_LIST_EVENT, () =>
            fetchPromoPosts().then(setPosts),
        );

        return () => {
            removeSubscriber(refreshListSubscriber);
        };
    }, []);

    return { setPosts, posts: postsToShow };
};
